import type { CodeFieldKey, CodePicks, RegistrySlot, ResinContext } from "./types";

export interface CodeFieldSpec {
  key: CodeFieldKey;
  slot: RegistrySlot | null;
  label: (resinFamilyCode: string) => string;
  anyLabel: (resinFamilyCode: string) => string;
  emptyLabel: (resinFamilyCode: string) => string;
  countNoun: (resinFamilyCode: string) => string;
  filterPlaceholder: string;
}

export interface CodeChoiceGrade {
  label: string;
  searchTexts: string[];
}

export interface CodeChoice {
  value: string;
  code: string;
  readsAs: string;
  meta: string;
  grades: CodeChoiceGrade[];
}

export interface CodeChoiceMatch {
  choice: CodeChoice;
  matchedGrades: CodeChoiceGrade[];
}

const REGISTRY_FILTER_PLACEHOLDER = "Filter by code or Reads As";

export const CODE_FIELDS: CodeFieldSpec[] = [
  {
    key: "designationId",
    slot: null,
    label: (resinFamilyCode) => `${resinFamilyCode} resin code`,
    anyLabel: (resinFamilyCode) => `Any ${resinFamilyCode} resin code`,
    emptyLabel: (resinFamilyCode) => `No ${resinFamilyCode} resin codes yet`,
    countNoun: (resinFamilyCode) => `${resinFamilyCode} resin code`,
    filterPlaceholder: "Filter by code, Reads As, tradename or supplier",
  },
  {
    key: "baseCodeId",
    slot: "Base",
    label: () => "Base code",
    anyLabel: () => "Any base",
    emptyLabel: (resinFamilyCode) => `No ${resinFamilyCode} base codes yet`,
    countNoun: () => "base code",
    filterPlaceholder: REGISTRY_FILTER_PLACEHOLDER,
  },
  {
    key: "additiveCodeId",
    slot: "Additive",
    label: () => "Additive code",
    anyLabel: () => "No additive picked",
    emptyLabel: () => "No additive codes yet",
    countNoun: () => "additive code",
    filterPlaceholder: REGISTRY_FILTER_PLACEHOLDER,
  },
  {
    key: "colorCodeId",
    slot: "Color",
    label: () => "Color code",
    anyLabel: () => "No color picked",
    emptyLabel: () => "No color codes yet",
    countNoun: () => "color code",
    filterPlaceholder: REGISTRY_FILTER_PLACEHOLDER,
  },
];

export const CODE_FIELD_KEYS: CodeFieldKey[] = CODE_FIELDS.map((spec) => spec.key);

const REGISTRY_FIELDS = CODE_FIELDS.filter(
  (spec): spec is CodeFieldSpec & { slot: RegistrySlot } => spec.slot !== null,
);

export function isCodeFieldKey(key: string): key is CodeFieldKey {
  return (CODE_FIELD_KEYS as string[]).includes(key);
}

export function codeFieldSpec(key: CodeFieldKey): CodeFieldSpec {
  return CODE_FIELDS.find((spec) => spec.key === key)!;
}

export function emptyCodePicks(): CodePicks {
  return { designationId: "", baseCodeId: "", additiveCodeId: "", colorCodeId: "" };
}

function presentTexts(values: (string | null | undefined)[]): string[] {
  return values.filter((value): value is string => Boolean(value));
}

export function codeChoices(resin: ResinContext, spec: CodeFieldSpec): CodeChoice[] {
  if (spec.slot === null) {
    return resin.resinCodes.designations.map(({ designation, gradeLinks }) => ({
      value: String(designation.designationId),
      code: designation.designationCode,
      readsAs: designation.readsAs,
      meta: `${designation.gradeCount} ${designation.gradeCount === 1 ? "grade" : "grades"}`,
      grades: gradeLinks.map((gradeLink) => ({
        label: `${gradeLink.tradename} · ${gradeLink.supplier}`,
        searchTexts: presentTexts([
          gradeLink.tradename,
          gradeLink.supplier,
          gradeLink.processingApplication,
          gradeLink.supplierCode,
          gradeLink.tradenameCode,
        ]),
      })),
    }));
  }
  return resin.registryCodes[spec.slot].map((approvedCode) => ({
    value: String(approvedCode.approvedCodeId),
    code: approvedCode.code,
    readsAs: approvedCode.readsAs,
    meta: approvedCode.resinFamilyCode ? `${approvedCode.resinFamilyCode} only` : "Any resin",
    grades: [],
  }));
}

function containsText(value: string, searchText: string): boolean {
  return value.toLowerCase().includes(searchText);
}

export function filterCodeChoices(choices: CodeChoice[], filterText: string): CodeChoiceMatch[] {
  const searchText = filterText.trim().toLowerCase();
  if (!searchText) return choices.map((choice) => ({ choice, matchedGrades: [] }));
  const matches: CodeChoiceMatch[] = [];
  for (const choice of choices) {
    const choiceMatches = containsText(choice.code, searchText) || containsText(choice.readsAs, searchText);
    const matchedGrades = choiceMatches
      ? []
      : choice.grades.filter((grade) => grade.searchTexts.some((text) => containsText(text, searchText)));
    if (choiceMatches || matchedGrades.length) matches.push({ choice, matchedGrades });
  }
  return matches;
}

export function designationPartCodes(
  resin: ResinContext,
  designationId: string,
): Record<RegistrySlot, string[]> {
  const partCodes: Record<RegistrySlot, string[]> = { Base: [], Additive: [], Color: [] };
  const found = resin.resinCodes.designations.find(
    ({ designation }) => String(designation.designationId) === designationId,
  );
  for (const part of [...(found?.designation.parts ?? [])].sort(
    (first, second) => first.partPosition - second.partPosition,
  )) {
    if (part.slotName === "Base" || part.slotName === "Additive" || part.slotName === "Color") {
      partCodes[part.slotName].push(part.code);
    }
  }
  return partCodes;
}

function registryIdForCode(resin: ResinContext, slot: RegistrySlot, code: string | undefined): string {
  if (!code) return "";
  const approvedCode = resin.registryCodes[slot].find((candidate) => candidate.code === code);
  return approvedCode ? String(approvedCode.approvedCodeId) : "";
}

export function applyCodePick<Picks extends Partial<CodePicks>>(
  picks: Picks,
  key: CodeFieldKey,
  value: string,
  resin: ResinContext,
): Picks {
  const updated = { ...picks, [key]: value };
  if (key === "designationId") {
    if (!value) return updated;
    const partCodes = designationPartCodes(resin, value);
    for (const spec of REGISTRY_FIELDS) {
      updated[spec.key] = registryIdForCode(resin, spec.slot, partCodes[spec.slot][0]);
    }
    return updated;
  }
  const designationId = picks.designationId;
  if (designationId) {
    const slot = codeFieldSpec(key).slot!;
    const expectedValue = registryIdForCode(resin, slot, designationPartCodes(resin, designationId)[slot][0]);
    if (value !== expectedValue) updated.designationId = "";
  }
  return updated;
}

export function extraAdditiveCodes(resin: ResinContext, designationId: string): string[] {
  return designationId ? designationPartCodes(resin, designationId).Additive.slice(1) : [];
}

function optionalId(value: string | undefined): number | null {
  return value ? Number(value) : null;
}

export function codePickBody(picks: Partial<CodePicks>) {
  return {
    designationId: optionalId(picks.designationId),
    baseCodeId: optionalId(picks.baseCodeId),
    additiveCodeId: optionalId(picks.additiveCodeId),
    colorCodeId: optionalId(picks.colorCodeId),
  };
}

type SavedCodeIds = Partial<Record<CodeFieldKey, number | null>>;

export function codePicksFromRecord(record: SavedCodeIds): CodePicks {
  const picks = emptyCodePicks();
  for (const key of CODE_FIELD_KEYS) {
    const savedId = record[key];
    picks[key] = savedId === null || savedId === undefined ? "" : String(savedId);
  }
  return picks;
}
